import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Pure copy for an agent's browser and its live screen (ADR-023 §9, D666): the Browser region,
// the live view, the hand-over and the machine card's sites line. No React, no I/O.

export type BrowserCatalog = typeof EN;

const EN = {
  screenTitleLive: "Live screen · {product}",
  browserTitle: "Browser",
  browserDescription: "The agent's own browser. Watch it live, and sign in there when a site asks.",
  screenAsleep: "Asleep",
  screenWaking: "Waking",
  screenLive: "Live",
  agentDrives: "{name} drives",
  memberDrives: "You have the screen",
  askSignIn: "{name} needs you: sign in to {site}",
  askWhy: "{name} needs you: {why}",
  askSignInHint: "Take the screen, sign in on the page, then press Done. {name} goes on from there.",
  askHint: "Take the screen, do what it asks, then press Done. {name} goes on from there.",
  takeScreen: "Take the screen",
  watch: "Watch",
  tabsLabel: "Open tabs",
  noTabs: "No tabs open yet.",
  browserAsleepHint: "Its machine is asleep. Watch wakes it, so you can see the screen.",
  browserWakingHint: "Its machine is waking. The screen goes live in a moment.",
  siteLinkedIn: "LinkedIn",
  siteInstagram: "Instagram",
  siteWeb: "The web",
  siteSignedIn: "{site} · signed in",
  siteNotYet: "{site} · not yet",
  sitesLabel: "Signed in on this machine",
  liveTitle: "Live screen",
  liveScreenLabel: "{name}'s screen",
  takeControl: "Take control",
  done: "Done",
  backToAgent: "Back to the agent",
  liveStatusLive: { one: "Live · 1 watching", other: "Live · {count} watching" },
  liveStatusWaking: "Waking the machine…",
  liveStatusAsleep: "The agent's machine is asleep.",
  liveStatusConnecting: "Connecting to the screen…",
  liveStatusEnded: "The screen stopped: the agent went to sleep. Watch again to wake it.",
  overlayWaking: "Waking the machine…",
  overlayConnecting: "Connecting to the screen…",
  overlayNotLive: "Not live",
  controlHint: "You have the keyboard and mouse. Press Done to hand the screen back.",
  watchHint: "You are watching. Take control to use the keyboard and mouse.",
  couldNotWatch: "Could not open the screen",
  couldNotTakeControl: "Could not take the screen",
  couldNotHandBack: "Could not hand the screen back",
  handedBack: "{name} has the screen again",
} as const;

const ES: Translation<BrowserCatalog> = {
  screenTitleLive: "Pantalla en vivo · {product}",
  browserTitle: "Navegador",
  browserDescription:
    "El navegador propio del agente. Míralo en vivo e inicia sesión ahí cuando un sitio lo pida.",
  screenAsleep: "Dormida",
  screenWaking: "Despertando",
  screenLive: "En vivo",
  agentDrives: "{name} lo maneja",
  memberDrives: "Tienes la pantalla",
  askSignIn: "{name} te necesita: inicia sesión en {site}",
  askWhy: "{name} te necesita: {why}",
  askSignInHint: "Toma la pantalla, inicia sesión en la página y pulsa Listo. {name} sigue desde ahí.",
  askHint: "Toma la pantalla, haz lo que pide y pulsa Listo. {name} sigue desde ahí.",
  takeScreen: "Tomar la pantalla",
  watch: "Ver",
  tabsLabel: "Pestañas abiertas",
  noTabs: "Aún no hay pestañas abiertas.",
  browserAsleepHint: "Su máquina está dormida. Ver la despierta para que puedas ver la pantalla.",
  browserWakingHint: "Su máquina está despertando. La pantalla estará en vivo en un momento.",
  siteLinkedIn: "LinkedIn",
  siteInstagram: "Instagram",
  siteWeb: "La web",
  siteSignedIn: "{site} · sesión iniciada",
  siteNotYet: "{site} · aún no",
  sitesLabel: "Sesiones en esta máquina",
  liveTitle: "Pantalla en vivo",
  liveScreenLabel: "La pantalla de {name}",
  takeControl: "Tomar el control",
  done: "Listo",
  backToAgent: "Volver al agente",
  liveStatusLive: { one: "En vivo · 1 mirando", other: "En vivo · {count} mirando" },
  liveStatusWaking: "Despertando la máquina…",
  liveStatusAsleep: "La máquina del agente está dormida.",
  liveStatusConnecting: "Conectando con la pantalla…",
  liveStatusEnded: "La pantalla se detuvo: el agente se durmió. Vuelve a verla para despertarlo.",
  overlayWaking: "Despertando la máquina…",
  overlayConnecting: "Conectando con la pantalla…",
  overlayNotLive: "No está en vivo",
  controlHint: "Tienes el teclado y el ratón. Pulsa Listo para devolver la pantalla.",
  watchHint: "Estás mirando. Toma el control para usar el teclado y el ratón.",
  couldNotWatch: "No pudimos abrir la pantalla",
  couldNotTakeControl: "No pudimos tomar la pantalla",
  couldNotHandBack: "No pudimos devolver la pantalla",
  handedBack: "{name} tiene la pantalla de nuevo",
};

export const BROWSER_CATALOGS: Catalogs<BrowserCatalog> = { en: EN, es: ES };
